import { useCommoditiesQuery } from "@/pages/sy/hooks/queries/useCommoditiesQuery";
import { ErrorModal } from "@dexteel/mesf-core";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

interface Props {
  commodityId: number | null;
  value: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

export const SubstituteCommoditiesSelector = ({
  commodityId,
  value,
  onChange,
  disabled = false,
}: Props) => {
  const [error, setError] = useState("");

  const { data: allCommodities = [], isLoading } = useCommoditiesQuery(true);

  // Exclude the current commodity being edited
  const commodities = allCommodities.filter(
    (c) => c.CommodityId !== commodityId,
  );

  const selectedCommodities = commodities.filter((c) =>
    value.includes(c.CommodityId as number),
  );

  return (
    <>
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Substitute Commodities
        </Typography>
        <Autocomplete
          multiple
          options={commodities}
          getOptionLabel={(option) => option.CommodityName}
          value={selectedCommodities}
          onChange={(_, newValue) => {
            onChange(newValue.map((c) => c.CommodityId as number));
          }}
          disabled={disabled || isLoading}
          loading={isLoading}
          isOptionEqualToValue={(option, val) =>
            option.CommodityId === val.CommodityId
          }
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => (
              <Chip
                {...getTagProps({ index })}
                key={option.CommodityId}
                label={option.CommodityName}
                sx={{
                  backgroundColor: option.CommodityColor || undefined,
                  color: option.CommodityColor ? "#fff" : undefined,
                }}
                size="small"
              />
            ))
          }
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.CommodityId}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "4px",
                  backgroundColor: option.CommodityColor || "#ccc",
                  mr: 1,
                  flexShrink: 0,
                }}
              />
              {option.CommodityName}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              size="small"
              placeholder="Select substitute commodities..."
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
        />
      </Box>
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
